// FRONT-3249 (behind eddy.model-picker-redesign): the "All models" window for a
// GenAI Hub workspace, built for the people who use EDDY, not for its admins.
// One row per model on one cloud — "Claude Haiku 5.5 on AWS, EU" — saying in
// plain words what it is good for, how good, fast and dear it is, and, in the
// row's own title, the one thing a company really has to decide: where it runs.
// A row IS the exact model it stands for, so nothing needs choosing inside it:
// click a row to select it, then the blue Save at the bottom applies the choice
// to this chat. Two icons beside each row act at once: the star keeps the row in
// the composer menu, the tick makes it the person's default. Above the list, fixed while it scrolls: the current
// selection, then the filters (sort, region, maker, a star that shows only the
// favourites) with the option count. Logic lives in `hub-picker-logic.ts`.

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDownIcon, CheckCircleIcon, StarIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { ProviderBadge, RegionBadge, type Region } from '~/components/model-badges';
import type { PickerModelEntry } from './model-picker-logic';
import {
  PICKER_SORTS,
  filterOptions,
  groupPickerModels,
  meterSegments,
  pickerRows,
  placeLabel,
  priceLevel,
  regionName,
  visibleGroups,
  type PickerFilters,
  type PickerRow,
  type PickerSort,
} from './hub-picker-logic';

/**
 * The chooser's one control look: flat, no pill inside a pill. The active one is plain gray, and anything
 * you can press shows a subtle border when the pointer is on it.
 */
const CHIP =
  'inline-flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1 text-xs font-medium transition-colors outline-none hover:border-border focus-visible:ring-2 focus-visible:ring-ring/50';
const CHIP_ACTIVE = 'bg-foreground/10 text-foreground';
const CHIP_IDLE = 'text-muted-foreground hover:text-foreground';

/** A row of flat chips — one active, gray — replacing the Segmented track-and-pill. */
function ChipGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; testId: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex items-center gap-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          data-testid={o.testId}
          onClick={() => onChange(o.value)}
          className={cn(CHIP, o.value === value ? CHIP_ACTIVE : CHIP_IDLE)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface HubModelPickerProps {
  models: readonly PickerModelEntry[];
  /** The Hub list could not be loaded at all — said in words, never shown as an empty activation. */
  unavailable?: boolean;
  starred: readonly string[];
  /** The model the chat uses right now (its pick or the default). */
  activeId: string | null;
  defaultId: string | null;
  /** Save with a different model selected: apply it to this chat. */
  onPick: (id: string) => void;
  /** Save was pressed (whether or not the choice changed): the host closes the chooser. */
  onDone?: () => void;
  /** Where Save stands when the host has a place for it (the composer's button row); without one — undefined or null — it sits beside the filters. */
  saveSlot?: HTMLElement | null;
  /** Where the current-selection header stands when the host has a place for it (the composer's footer); without one — undefined or null — it sits above the filters. */
  selectionSlot?: HTMLElement | null;
  onToggleStar: (id: string) => void;
  onSetDefault?: (id: string) => void;
  /** Test seams. */
  initialSort?: PickerSort;
  initialFilters?: PickerFilters;
}

export function HubModelPicker({
  models,
  unavailable,
  starred,
  activeId,
  defaultId,
  onPick,
  onDone,
  saveSlot,
  selectionSlot,
  onToggleStar,
  onSetDefault,
  initialSort = 'recommended',
  initialFilters = { region: 'all', maker: 'all' },
}: HubModelPickerProps) {
  const [sort, setSort] = useState<PickerSort>(initialSort);
  const [filters, setFilters] = useState<PickerFilters>(initialFilters);
  // The row the person has selected, not yet saved; it starts on the model the chat uses.
  const [selectedId, setSelectedId] = useState<string | null>(activeId);
  const groups = useMemo(() => groupPickerModels(models), [models]);
  const options = useMemo(() => filterOptions(groups), [groups]);
  const rows = pickerRows(visibleGroups(groups, filters, sort, starred));
  const noFavourites = !!filters.favouritesOnly && starred.length === 0;
  // The selection is shown whatever the filters hide, so it is looked up in every row.
  const selectedRow = useMemo(() => pickerRows(groups).find((r) => r.place.id === selectedId) ?? null, [groups, selectedId]);
  const selectionHeader = <SelectionHeader row={selectedRow} activeId={activeId} defaultId={defaultId} compact={!!selectionSlot} />;
  const saveButton = (
    <Button
      size="sm"
      data-testid="model-picker-save"
      className="h-8 px-4"
      onClick={() => {
        if (selectedId && selectedId !== activeId) onPick(selectedId);
        onDone?.();
      }}
    >
      Save
    </Button>
  );

  if (unavailable) {
    return (
      <p data-testid="model-picker-hub-unavailable" className="py-10 text-center text-sm text-muted-foreground">
        Your workspace's model list from GenAI Hub couldn't be loaded. Try again in a moment — your chats keep running on their current model.
      </p>
    );
  }
  if (groups.length === 0) {
    return (
      <p data-testid="model-picker-empty" className="py-10 text-center text-sm text-muted-foreground">
        No models are activated for this workspace in GenAI Hub. Ask your admin to activate some.
      </p>
    );
  }

  return (
    <div data-testid="model-picker-hub" className="flex min-h-0 flex-1 flex-col gap-3">
      {/* The current selection: fixed above the filters, or between the host's footer buttons when it gives a place for it. */}
      {!selectionSlot && selectionHeader}
      {selectionSlot ? createPortal(selectionHeader, selectionSlot) : null}

      <div data-testid="model-picker-filters" className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border pb-3">
        <ChipGroup
          label="Sort models"
          value={sort}
          onChange={setSort}
          options={PICKER_SORTS.map((p) => ({ value: p.id, label: p.label, testId: `model-picker-sort-${p.id}` }))}
        />
        {options.regions.length > 1 && (
          <ChipGroup
            label="Where the model runs"
            value={filters.region}
            onChange={(region) => setFilters((f) => ({ ...f, region }))}
            options={[
              { value: 'all' as const, label: 'Anywhere', testId: 'model-picker-region-all' },
              ...options.regions.map((r) => ({ value: r, label: regionName(r)!, testId: `model-picker-region-${r}` })),
            ]}
          />
        )}
        {options.makers.length > 1 && (
          <MakerFilter makers={options.makers} value={filters.maker} onChange={(maker) => setFilters((f) => ({ ...f, maker }))} />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          data-testid="model-picker-favourites-only"
          aria-pressed={!!filters.favouritesOnly}
          aria-label="Favourites only"
          tooltip={filters.favouritesOnly ? 'Showing only favourites' : 'Show only favourites'}
          onClick={() => setFilters((f) => ({ ...f, favouritesOnly: !f.favouritesOnly }))}
          className={cn('border border-transparent hover:border-border hover:bg-transparent', filters.favouritesOnly ? 'bg-foreground/10 text-amber-500 hover:bg-foreground/10' : 'text-muted-foreground')}
        >
          <StarIcon size={16} weight={filters.favouritesOnly ? 'fill' : 'regular'} />
        </Button>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="model-picker-count">
          {rows.length === 1 ? '1 option' : `${rows.length} options`}
        </span>
        {/* No slot (none given, or not mounted): Save sits here, so it is never nowhere. */}
        {!saveSlot && saveButton}
      </div>

      <div data-testid="model-picker-list" className="min-h-0 flex-1 overflow-y-auto pr-1">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
            <p data-testid={noFavourites ? 'model-picker-no-favourites' : undefined}>
              {noFavourites ? 'You have no favourites yet. Star a model to keep it here and in your menu.' : 'No model matches these filters.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => setFilters({ region: 'all', maker: 'all', favouritesOnly: false })}>
              Show all models
            </Button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-x-6 xl:grid-cols-2">
            {rows.map((row) => (
              <ModelRow
                key={row.place.id}
                row={row}
                activeId={activeId}
                defaultId={defaultId}
                starred={starred}
                selected={row.place.id === selectedId}
                onSelect={setSelectedId}
                onToggleStar={onToggleStar}
                onSetDefault={onSetDefault}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Save rides in the host's button row, where Send stands. */}
      {saveSlot ? createPortal(saveButton, saveSlot) : null}

    </div>
  );
}

/**
 * The current selection: what is selected right now (not yet saved when it differs from what the chat uses).
 * Fixed above the filters, or — `compact`, in a footer — one short line that gives way to the buttons around it.
 */
function SelectionHeader({
  row,
  activeId,
  defaultId,
  compact,
}: {
  row: PickerRow | null;
  activeId: string | null;
  defaultId: string | null;
  compact: boolean;
}) {
  const box = compact
    ? 'flex min-w-0 max-w-full items-center gap-x-2 rounded-md px-3 py-1 text-xs'
    : 'flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-3 py-2 text-sm';
  if (!row) {
    return (
      <div data-testid="model-picker-selection" className={cn(box, compact ? 'text-muted-foreground' : 'border border-border text-muted-foreground')}>
        <span className="truncate">No model selected{compact ? '' : ' — pick one below.'}</span>
      </div>
    );
  }
  const { group: g, place } = row;
  return (
    <div data-testid="model-picker-selection" className={cn(box, 'bg-foreground/[0.07]')}>
      <ProviderBadge id={place.id} />
      <span className="truncate font-semibold">{g.name}</span>
      {g.legacy && <span className="shrink-0 text-muted-foreground">(legacy)</span>}
      <span className="inline-flex min-w-0 shrink-0 items-center gap-1.5 text-muted-foreground">
        on
        {place.region && <RegionBadge region={place.region as Region} />}
        <span className="truncate text-foreground">{placeLabel(place)}</span>
      </span>
      {place.id === activeId && <Tag tone="primary">In use</Tag>}
      {place.id === defaultId && <Tag>Your default</Tag>}
    </div>
  );
}

function MakerFilter({ makers, value, onChange }: { makers: string[]; value: string; onChange: (maker: string) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="model-picker-maker" className={cn(CHIP, value === 'all' ? CHIP_IDLE : CHIP_ACTIVE)}>
          {value === 'all' ? 'All makers' : value}
          <CaretDownIcon size={11} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          <DropdownMenuRadioItem value="all">All makers</DropdownMenuRadioItem>
          {makers.map((m) => (
            <DropdownMenuRadioItem key={m} value={m}>
              {m}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ModelRow({
  row: { group: g, place },
  activeId,
  defaultId,
  starred,
  selected,
  onSelect,
  onToggleStar,
  onSetDefault,
}: {
  row: PickerRow;
  activeId: string | null;
  defaultId: string | null;
  starred: readonly string[];
  selected: boolean;
  onSelect: (id: string) => void;
  onToggleStar: (id: string) => void;
  onSetDefault?: (id: string) => void;
}) {
  const inUse = place.id === activeId;
  const isDefault = place.id === defaultId;
  const isStarred = starred.includes(place.id);
  const where = placeLabel(place);
  return (
    <li
      data-testid={`model-picker-row-${place.id}`}
      data-selected={selected ? 'true' : undefined}
      // A click anywhere on the row selects it; the keyboard reaches the same through the title button.
      onClick={() => onSelect(place.id)}
      className={cn(
        'flex cursor-pointer flex-col gap-3 rounded-md border-b border-border px-2 py-4 transition-colors hover:ring-1 hover:ring-border',
        selected && 'bg-foreground/[0.07]',
      )}
    >
      <div className="flex items-start gap-3">
        <ProviderBadge id={place.id} />
        <button
          type="button"
          aria-pressed={selected}
          data-testid={`model-picker-select-${place.id}`}
          onClick={() => onSelect(place.id)}
          className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="flex flex-wrap items-center gap-x-1.5 text-sm font-semibold">
              {g.name}
              {g.legacy && <span className="font-normal text-muted-foreground">(legacy)</span>}
              <span className="inline-flex items-center gap-1.5 font-normal text-muted-foreground">
                on
                {place.region && <RegionBadge region={place.region as Region} />}
                <span className="text-foreground">{where}</span>
              </span>
            </h3>
            {inUse && <Tag tone="primary">In use</Tag>}
            {isDefault && <Tag>Your default</Tag>}
            {g.recommended && <Tag>Recommended</Tag>}
            {g.status === 'preview' && <Tag>Preview</Tag>}
            {g.status === 'retiring' && <Tag tone="warn">Being retired</Tag>}
          </div>
          <p className="text-xs text-muted-foreground">
            {[g.maker, g.goodFor].filter(Boolean).join(' — ')}
            {g.agents ? '' : ' Not for agent tasks.'}
          </p>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          data-testid={`model-picker-star-${place.id}`}
          aria-pressed={isStarred}
          aria-label={isStarred ? `Remove ${g.name} on ${where} from your menu` : `Keep ${g.name} on ${where} in your menu`}
          tooltip={isStarred ? 'In your menu' : 'Keep in your menu'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar(place.id);
          }}
          className={cn('border border-transparent hover:border-border hover:bg-transparent', isStarred ? 'text-amber-500' : 'text-muted-foreground')}
        >
          <StarIcon size={16} weight={isStarred ? 'fill' : 'regular'} />
        </Button>
        {/* Next to the star: make this the default. Already the default, it shows as set and has nothing left to do. */}
        {onSetDefault && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            data-testid={`model-picker-make-default-${place.id}`}
            aria-pressed={isDefault}
            disabled={isDefault}
            aria-label={isDefault ? `${g.name} on ${where} is your default` : `Make ${g.name} on ${where} your default`}
            tooltip={isDefault ? 'Your default' : 'Make default'}
            onClick={(e) => {
              e.stopPropagation();
              onSetDefault(place.id);
            }}
            className={cn('border border-transparent hover:border-border hover:bg-transparent', isDefault ? 'text-foreground disabled:opacity-100' : 'text-muted-foreground')}
          >
            <CheckCircleIcon size={16} weight={isDefault ? 'fill' : 'regular'} />
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-3 gap-3 text-xs">
        <Meter label="Quality" segments={meterSegments(g.quality)} />
        <Meter label="Speed" segments={meterSegments(g.speed)} />
        <Price level={priceLevel(g.cost)} />
      </dl>
    </li>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: 'primary' | 'warn' }) {
  return (
    <span
      className={cn(
        'rounded-full border px-2 py-px text-[11px]',
        tone === 'primary' && 'border-transparent bg-foreground/10 text-foreground',
        tone === 'warn' && 'border-amber-500/40 text-amber-600',
        !tone && 'border-border text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}

function Meter({ label, segments }: { label: string; segments: number | null }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>
        {segments === null ? (
          <span className="text-muted-foreground">Not published</span>
        ) : (
          <span className="flex gap-0.5" role="img" aria-label={`${label}: ${segments} of 5`}>
            {[1, 2, 3, 4, 5].map((i) => (
              <span key={i} className={cn('h-1.5 w-5 rounded-full', i <= segments ? 'bg-foreground/70' : 'bg-muted')} />
            ))}
          </span>
        )}
      </dd>
    </div>
  );
}

function Price({ level }: { level: 1 | 2 | 3 | 4 | null }) {
  const words = { 1: 'Low', 2: 'Moderate', 3: 'High', 4: 'Very high' } as const;
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-muted-foreground">Price</dt>
      <dd>
        {level === null ? (
          <span className="text-muted-foreground">Not published</span>
        ) : (
          <span aria-label={`Price: ${words[level]}`}>
            <span className="font-medium text-foreground">{'€'.repeat(level)}</span>
            <span className="text-muted-foreground/40">{'€'.repeat(4 - level)}</span>
          </span>
        )}
      </dd>
    </div>
  );
}
